import type React from 'react';

import type { ThemeUIStyleObject } from '@theme-ui/core';

import { Separator } from '@change/ds-core-ui';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { Link } from '@change-corgi/design-system-legacy/components/actions';
import { Card } from '@change-corgi/design-system-legacy/components/content';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { iconInfoOutline } from '@change-corgi/design-system-legacy/icons';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import { useRecommendations as useRecommendationsContext } from 'src/app/pages/starterDashboardV2/shared/contexts';
import { useDashboardReskinExperiment } from 'src/app/pages/starterDashboardV2/shared/hooks';
import type { WaypointCallbackArgs } from 'src/app/shared/components/layout/lazy';
import { Waypoint } from 'src/app/shared/components/scroll';

import { RecommendationsCarousel } from './components/RecommendationsCarousel';
import { ShareUrl } from './components/ShareUrl';
import { useRecommendations } from './hooks/useRecommendations';
import { RecommendationsVariant2Reskin } from './variant2_new';

import type { Props } from './index';

const styles: Record<string, ThemeUIStyleObject> = {
	betaTag: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		padding: '2px 8px',
	},
	mainCard: {
		border: 'unset',
		borderRadius: 'big',
		display: 'flex',
		flexDirection: 'column',
		gap: '24px',
		paddingY: '24px',
		width: '100%',
	},
	separator: {
		borderColor: 'neutral-grey100',
		borderStyle: 'solid none none none',
		display: ['inline', 'none'],
		marginTop: 8,
		marginBottom: 8,
	},
};

export const RecommendationsVariant2: React.FC<Omit<Props, 'slug'>> = ({ viewContext }) => {
	const {
		data: { shareRecommendationsLoading, shouldDisplayShareRecommendations, shareRecommendations },
	} = useRecommendationsContext();

	const { actions } = useRecommendations();
	const {
		data: { isEnabled },
	} = useDashboardReskinExperiment();
	const { translateHtml } = useI18n();

	if (isEnabled) {
		return <RecommendationsVariant2Reskin viewContext={viewContext} />;
	}

	if (!shouldDisplayShareRecommendations) {
		return null;
	}

	const firstRedditRec = shareRecommendations.reddit.at(0);
	const firstFacebookRec = shareRecommendations.facebook.at(0);

	return (
		<Box
			as="section"
			id="recommendations-section"
			aria-labelledby="recommendations-title"
			data-testid="recommendations-variant2"
			className="scroll-mt-20 md:scroll-mt-18"
		>
			<Waypoint
				onPositionChange={(waypointCallbackArgs: WaypointCallbackArgs) => {
					actions.trackView(
						waypointCallbackArgs,
						firstFacebookRec?.metadata.source,
						firstRedditRec?.metadata.source,
						viewContext,
					);
				}}
			/>
			<Card backgroundColor="white" sx={styles.mainCard}>
				<Flex sx={{ flex: '1 0 0', flexDirection: 'column', gap: '16px', paddingX: '24px' }}>
					<Heading as="h2" size="h4" id="recommendations-title" data-qa="recommendations-title">
						Share in recommended groups
					</Heading>
					<Text size="default">
						{translateHtml('corgi.petition.starter_dashboard.recommendations.description_variant')}&nbsp;
						<Link
							variant="primary"
							to="#"
							onClick={async (e) => {
								e.preventDefault();
								await actions.handleTipClick();
							}}
							sx={{ display: 'inline-flex', color: 'primary-black', fontWeight: '400', alignItems: 'center' }}
							data-qa="recommendations-tip-button"
						>
							<Text size="default">
								<Flex sx={{ alignItems: 'center', gap: 3 }}>
									<Translate value="corgi.petition.starter_dashboard.recommendations.sharing_tips" />
									<Icon icon={iconInfoOutline} color="currentColor" size="m" />
								</Flex>
							</Text>
						</Link>
					</Text>
					<div className="max-w-full md:max-w-[50%]">
						<ShareUrl viewContext={viewContext} />
					</div>
				</Flex>
				<div className="mx-6 my-0">
					<Separator className="m-0" />
				</div>
				<RecommendationsCarousel
					loading={shareRecommendationsLoading}
					shareRecommendations={shareRecommendations}
					viewContext={viewContext}
				/>
			</Card>
		</Box>
	);
};
